import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';

import { usePrevious } from 'hooks';
import { usersActions } from 'state/modules/users';
import { Autocomplete } from 'components/FormFields';
import { truncateKeywordSpaces } from 'utils/utils';

const UsersSelect = ({
  id,
  type,
  label,
  style,
  datacy,
  margin,
  variant,
  disabled,
  selector,
  firstLoad,
  onChange,
  customParams,
  field: { name, value },
  form: { errors, touched, setFieldValue },
  ...props
}) => {
  const dispatch = useDispatch();
  const [defaultValue, setDefaultValue] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    ...customParams,
  });

  const users = useSelector((state) => state.users[type] || []);
  const loading = useSelector(
    (state) => state.users[type === 'users' ? 'loading' : `${type}Loading`]
  );
  const prevvalue = usePrevious(value);
  const prev = usePrevious(customParams);

  /**
   * Fetch vehicles list
   * @param {*} params
   */
  const fetchUsers = useCallback(
    debounce((params = {}) => {
      const newParams = {
        ...params,
      };
      setParams({ ...newParams, account_id: undefined });
      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
      }
      dispatch(usersActions.fetchUsers(newParams, type));
    }, 1000),
    []
  );

  useEffect(() => {
    if (prev && customParams && JSON.stringify(prev) !== JSON.stringify(customParams)) {
      fetchUsers({ ...params, ...customParams });
    }
  }, [customParams]); // eslint-disable-line

  useEffect(() => {
    if (prevvalue !== value) {
      const defaultVal = users.find((el) => el.id === parseInt(value));
      if (defaultVal) {
        setDefaultValue(defaultVal);
      } else if (value && !defaultValue?.id && !selector) {
        dispatch(usersActions.fetchUserById(value, type, true));
      } else {
        setDefaultValue(null);
      }
    }
  }, [value, users]); // eslint-disable-line

  useEffect(() => {
    if (prevvalue === value && users.length > 0) {
      const defaultVal = users.find(
        (el) => el.id === parseInt(value) || el.account_id === parseInt(value)
      );
      if (defaultVal) {
        setDefaultValue(defaultVal);
      }
    }
  }, [users]); // eslint-disable-line

  useEffect(() => {
    if (firstLoad) {
      const _params = { ...params };
      if (selector && value) {
        _params[selector] = value;
      }
      fetchUsers(_params);
    }
  }, [firstLoad, fetchUsers]); // eslint-disable-line

  return (
    <Autocomplete
      id={id}
      name={name}
      dataCy={datacy || id}
      style={style}
      label={label}
      margin={margin}
      fullwidth="true"
      variant={variant}
      loading={loading}
      disabled={disabled}
      clearOnBlur={false}
      value={defaultValue}
      options={users || []}
      inputProps={{
        ...props?.inputProps || {},
      }}
      getOptionLabel={(option) =>
        `${option?.first_name ? option?.first_name : ''} ${
          option.last_name ? option.last_name : ''
        } (${option?.email ? option?.email : ''})`
      }
      error={errors && errors[name] && touched && touched[name]}
      getOptionSelected={(option, value) => option.id === value?.id}
      onChange={(ev, val) => {
        setFieldValue(name, val?.id, val || '');
        if (onChange) {
          onChange(name, val);
        }
      }}
      onInputChange={(ev, val) => {
        const name = (val || '')?.split(' (');
        if (name?.length && name[0]?.length) {
          fetchUsers({ ...params, keyword: name[0] });
        }
      }}
      helperText={errors && errors[name] && touched && touched[name] && errors[name]}
      {...props}
    />
  );
};

UsersSelect.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  margin: PropTypes.string,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  style: PropTypes.object,
  customParams: PropTypes.object,
  inputProps: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

UsersSelect.defaultProps = {
  required: true,
  disabled: false,
  form: {
    errors: {},
    touched: {},
  },
  style: {},
  margin: 'normal',
  customParams: {},
  variant: 'standard',
  label: 'Select...',
};

export default UsersSelect;
